import type { Security } from '../types/Security';
import { InstrumentCompositionEnum } from '../types/types';
import { getSearchTerms } from './findSubstringMatches';
import { getSecurityIdentifierValues } from './getSecurityMetadata';
import { getGroup, getProductGroupLabel } from './security';

export type SecuritySearchMetadata = {
  /** Matched values that are not already visible in the display symbol or description. */
  matchedValues: string[];
  terms: string[];
};

function securityProductGroupLabel(security: Security): string | undefined {
  if (security.ProductType == null && security.Composition !== InstrumentCompositionEnum.Synthetic) {
    return undefined;
  }
  const label = getProductGroupLabel(getGroup(security));
  return label === '' ? undefined : label;
}

function containsTerm(value: string, term: string): boolean {
  return value.toLowerCase().includes(term);
}

function alreadyShown(value: string, displaySymbol: string, description: string): boolean {
  const normalized = value.toLowerCase();
  return displaySymbol.toLowerCase().includes(normalized) || description.toLowerCase().includes(normalized);
}

function pushUnshown(
  matchedValues: string[],
  seen: Set<string>,
  value: string,
  displaySymbol: string,
  description: string
) {
  if (seen.has(value) || alreadyShown(value, displaySymbol, description)) {
    return;
  }
  seen.add(value);
  matchedValues.push(value);
}

export function getSecuritySearchMetadata(
  security: Security,
  searchText: string,
  minMatchCharLength = 2,
  marketDisplayNameByName?: ReadonlyMap<string, string>,
  includeSedol = false
): SecuritySearchMetadata {
  const terms = getSearchTerms(searchText, minMatchCharLength);
  const displaySymbol = security.DisplaySymbol || security.Symbol;
  const description = security.Description ?? '';
  const matchedValues: string[] = [];
  const seen = new Set<string>();
  const rawSymbolMatchesUnshownTerm =
    security.Symbol !== displaySymbol &&
    terms.some(
      term =>
        containsTerm(security.Symbol, term) && !containsTerm(displaySymbol, term) && !containsTerm(description, term)
    );

  if (rawSymbolMatchesUnshownTerm) {
    pushUnshown(matchedValues, seen, security.Symbol, displaySymbol, description);
  }

  for (const identifier of getSecurityIdentifierValues(security, includeSedol)) {
    if (terms.some(term => containsTerm(identifier, term))) {
      pushUnshown(matchedValues, seen, identifier, displaySymbol, description);
    }
  }

  for (const market of security.Markets ?? []) {
    const displayName = marketDisplayNameByName?.get(market) ?? market;
    if (terms.some(term => containsTerm(displayName, term) || containsTerm(market, term))) {
      pushUnshown(matchedValues, seen, displayName, displaySymbol, description);
    }
  }

  const productLabel = securityProductGroupLabel(security);
  const productType = security.ProductType?.toLowerCase();
  if (
    productLabel != null &&
    terms.some(term => containsTerm(productLabel, term) || (productType != null && containsTerm(productType, term)))
  ) {
    pushUnshown(matchedValues, seen, productLabel, displaySymbol, description);
  }

  return { matchedValues, terms };
}
